import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from '@/lib/analytics';
import {
  ArrowRight,
  CheckCircle2,
  HeartPulse,
  ShieldCheck,
  Mail,
  Network,
  Lock,
  Globe,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const WHY_ORACLE_HEALTHCARE = [
  {
    icon: ShieldCheck,
    title: 'HIPAA-compliant, audit-sensitive workflows',
    body: 'Health systems answer to auditors and regulators. Every capture, coding change, approver, and attached document is recorded and exportable — with HIPAA-compliant handling for protected information.',
  },
  {
    icon: Network,
    title: 'Decentralized purchasing across facilities',
    body: 'Invoices arrive against many departments, cost centres, and facilities. EZ Cloud reads your Oracle Fusion business-unit hierarchy and routes each invoice to the right approver automatically.',
  },
  {
    icon: Globe,
    title: 'Clinical + corporate invoice complexity',
    body: 'PO and non-PO, credit notes, statements, proforma, reimbursements, urgent clinical supply — health-service AP spans a wide scenario range that generic OCR tools stumble on.',
  },
  {
    icon: Mail,
    title: 'Supplier-email overload, before the invoice',
    body: 'Payment-status chasing, missing PO numbers, duplicate submissions — the manual triage that consumes AP capacity. EZ Cloud resolves it upstream with live Oracle context.',
  },
];

const HOW_IT_WORKS = [
  {
    title: 'Oracle-supported integration',
    body: 'EZ Cloud posts into Oracle Fusion Cloud ERP using Oracle-supported methods — FBDI load with real-time validation over Oracle Integration Cloud (OIC) REST. No custom base-table writes, no brittle connector.',
  },
  {
    title: 'AI email exception prevention',
    body: "EZ Cloud's AI reads, classifies, and routes inbound supplier email before invoices reach the workflow — the upstream triage health-service AP teams do by hand today.",
  },
  {
    title: 'Capture, match, and post',
    body: 'AI capture, duplicate and active-vendor checks, and tolerance-aware three-way match — finished invoices post straight through to Oracle Fusion with the approval trail attached.',
  },
  {
    title: 'OCI-native, in-region deployment',
    body: 'Deploy in your own OCI tenancy via VCN peering — no public internet exposure — and keep data in-region for residency requirements (including Australia).',
  },
];

const FAQS = [
  {
    q: 'Does EZ Cloud work with Oracle Fusion Cloud ERP for healthcare?',
    a: 'Yes. EZ Cloud integrates with Oracle Fusion Cloud ERP using Oracle-supported methods — FBDI load with real-time validation over Oracle Integration Cloud (OIC) REST — and is published on the Oracle Reference Architecture for OCI. Health systems run it for AP capture, exception prevention, approval routing, and posting into Fusion.',
  },
  {
    q: 'Is it HIPAA-compliant? Is the environment secure?',
    a: 'EZ Cloud provides HIPAA-compliant workflows for healthcare customers and runs in a SOC 2 Type II-certified environment with role-based access and Azure AD single sign-on. It can be deployed in your own OCI tenancy via VCN peering, with no public internet exposure. Contact the security team about BAA specifics.',
  },
  {
    q: 'Can data stay in our region?',
    a: 'Yes. EZ Cloud can be deployed in-region in your OCI tenancy to meet data-residency requirements — including Australia — so protected information does not leave the jurisdictions you operate in.',
  },
  {
    q: 'How long does implementation take?',
    a: 'Typical timeline is 8–12 weeks. The critical-path items are supplier-master cleanup, approval-matrix mapping, and sandbox testing with a representative invoice sample. EZ Cloud does the integration work; you provide the Oracle access and sign-off.',
  },
];

const HealthcareOracleFusion = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle Fusion AP Automation for Healthcare | EZ Cloud"
        description="AP automation for health systems on Oracle Fusion Cloud ERP. HIPAA-compliant, OCI-native deployment, audit-ready, with AI email exception prevention — posted into Fusion using Oracle-supported integration methods."
        canonical="https://ezcloud.ai/solutions/healthcare-oracle-fusion"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/healthcare-oracle-fusion' },
          { name: 'Healthcare on Oracle Fusion', url: 'https://ezcloud.ai/solutions/healthcare-oracle-fusion' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <HeartPulse className="w-4 h-4" aria-hidden />
              Solutions · Healthcare × Oracle Fusion
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              AP automation for healthcare on Oracle Fusion Cloud ERP
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Health systems run AP under audit, across facilities, on a wide range of invoice
              scenarios. EZ Cloud automates it end-to-end on Oracle Fusion — from supplier-email
              intake to posting — using Oracle-supported integration methods.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-white/10 border border-white/20 text-sm text-white/90">
              <ShieldCheck className="w-4 h-4 text-accent" aria-hidden />
              <span>
                Published on the{' '}
                <a
                  href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline decoration-accent/50 underline-offset-2 hover:decoration-accent"
                >
                  Oracle Reference Architecture for OCI
                </a>
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" variant="accent" className="font-semibold" onClick={() => openCalendlyDemo()}>
                Book an Oracle AP demo
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/customers/health-system-oracle-fusion">Read the health-system story</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Manual-before framing */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              From manual to automated
            </p>
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              Most health services still run AP manually — keying invoices and working supplier email
              by hand. EZ Cloud automates that work on top of Oracle Fusion, and per Oracle's own
              technical case study helps companies capture, authorize, and pay invoices{' '}
              <a
                href="https://www.oracle.com/cloud/technical-case-studies/ez-cloud/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
              >
                up to 80% faster than on-premises AP
              </a>
              .
            </p>
          </div>
        </section>

        {/* Why Oracle healthcare AP is different */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Why healthcare AP on Oracle is different
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Generic AP automation struggles where healthcare finance lives — compliance, decentralized
              purchasing, and operational supplier traffic. EZ Cloud was engineered for it.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHY_ORACLE_HEALTHCARE.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              How EZ Cloud runs on Oracle Fusion
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {HOW_IT_WORKS.map((item) => (
                <Card key={item.title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Proof */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-3">
                <HeartPulse className="w-4 h-4" aria-hidden />
                Proof point
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                A major Australian health service, on Oracle Fusion
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                In a proof of concept on Oracle Fusion Cloud ERP, EZ Cloud's AI processed 2,500+
                supplier emails with zero errors and validated routing end-to-end — replacing manual
                AP triage across the health service's full range of invoice scenarios.
              </p>
              <Link
                to="/customers/health-system-oracle-fusion"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Read the full story
                <ArrowRight className="w-4 h-4 ml-1.5" aria-hidden />
              </Link>
            </Card>

            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-3">
                <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" aria-hidden />
                SOC 2 Type II-certified environment
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-3">
                <Lock className="w-5 h-5 text-primary flex-shrink-0" aria-hidden />
                OCI-native, no public internet
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-3">
                <Globe className="w-5 h-5 text-primary flex-shrink-0" aria-hidden />
                In-region data residency
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              See AP automation on your Oracle Fusion
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Book a 30-minute walkthrough — we'll run a real exception from supplier email to Fusion
              posting, against your invoice sample.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" variant="accent" className="font-semibold" onClick={() => openCalendlyDemo()}>
                Book an Oracle AP demo
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/industries/healthcare">Explore healthcare AP</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HealthcareOracleFusion;
